import React, { useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../config';
import { useTranslation } from '../../translations';
import { TextInput, SecondaryButton } from '../../ui/common';
import { poprawnyNip } from '../../utils/nip';

// Pole NIP z przyciskiem „Pobierz dane” (24.10.2026): nazwa, REGON i adres firmy z rejestru
// publicznego (GUS, zapasowo wykaz podatników VAT z MF) przez GET /api/company-lookup.
// Wypełnianie pól robi wołający (`onFound`), bo każdy formularz ma inne pola nabywcy.

// W wąskiej kolumnie (edytor faktury przy menu bocznym) przycisk schodzi pod pole, zamiast
// ucinać ostatnie cyfry NIP-u: pole nie zwęża się poniżej długości NIP-u z kreskami.
const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;

  > input { flex: 1 1 15ch; min-width: 15ch; }
  > button { flex-shrink: 0; white-space: nowrap; padding-inline: 14px; }
`;

const Status = styled.p`
  margin: 6px 0 0;
  font-size: var(--fs-xs, 0.78rem);
  line-height: 1.4;
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--danger)' : $tone === 'ok' ? 'var(--success)' : 'var(--text-muted)')};
`;

export default function NipLookupInput({ id, value, onChange, onFound, dataCy = 'nip-lookup' }) {
  const { t } = useTranslation();
  const [stan, setStan] = useState({ tone: null, text: '' });
  const [busy, setBusy] = useState(false);
  const nip = poprawnyNip(value);

  const pobierz = async () => {
    if (!nip) {
      setStan({ tone: 'error', text: t('companyLookup.invalidNip') });
      return;
    }
    setBusy(true);
    setStan({ tone: null, text: t('companyLookup.loading') });
    try {
      const res = await fetch(`${API_BASE}/api/company-lookup?nip=${nip}`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.company) {
        onFound(data.company);
        setStan({
          tone: 'ok',
          text: data.company.source === 'gus' ? t('companyLookup.foundGus') : t('companyLookup.foundVat'),
        });
      } else if (res.status === 404) {
        setStan({ tone: 'error', text: t('companyLookup.notFound') });
      } else if (res.status === 429) {
        setStan({ tone: 'error', text: t('companyLookup.tooMany') });
      } else if (res.status === 400) {
        setStan({ tone: 'error', text: t('companyLookup.invalidNip') });
      } else {
        setStan({ tone: 'error', text: t('companyLookup.unavailable') });
      }
    } catch (_) {
      setStan({ tone: 'error', text: t('companyLookup.unavailable') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Group>
        <TextInput
          id={id}
          value={value}
          inputMode="numeric"
          autoComplete="off"
          onChange={(e) => { onChange(e.target.value); if (stan.text) setStan({ tone: null, text: '' }); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!busy) pobierz(); } }}
          data-cy={`${dataCy}-input`}
        />
        <SecondaryButton
          type="button"
          onClick={pobierz}
          disabled={busy || !nip}
          title={nip ? t('companyLookup.buttonTitle') : t('companyLookup.invalidNip')}
          data-cy={`${dataCy}-button`}
        >
          {busy ? t('companyLookup.loadingShort') : t('companyLookup.button')}
        </SecondaryButton>
      </Group>
      {stan.text && <Status $tone={stan.tone} role="status" data-cy={`${dataCy}-status`}>{stan.text}</Status>}
    </>
  );
}
